import * as React from 'react';

import {renderElement} from '../../utils';
import {useSize} from '../../themes';
import {Text} from '../Text';
import {BadgeView} from './Badge.styles';

import type {ButtonPrefixSuffix} from '../Button';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

export type BadgeProps = {
  /**
   * Content for the `Badge`.
   */
  children: React.ReactNode;
  /**
   * The color of the `Badge`.
   */
  colorVariant?:
    | 'default'
    | 'primary'
    | 'green'
    | 'red'
    | 'orange'
    | 'purple'
    | 'pink'
    | 'yellow';
  /**
   * Determines the text size and spacing used in the `Badge`.
   */
  density?: 'comfortable' | 'compact';
  /**
   * Determines if the `Badge` has rounded borders. Defaults to `true`.
   */
  isRounded?: boolean;
  /**
   * Icon to show before the `Badge`'s text.
   */
  prefix?: ButtonPrefixSuffix;
  /**
   * The variant of the `Badge`. `default` is the default.
   */
  variant?: 'solid' | 'default';
} & Omit<HTMLAttributes, 'prefix'> &
  SpringTooling;

/**
 * # Badge
 *
 * Badges are sparingly used to highlight new or altered features within the product and to attract the attention of a user.
 *
 * ## Usage
 *
 * ```jsx
 * import React from 'react'
 * import {Badge} from '@packages/systems/spring';
 *
 * const Example = () => (
 *  <>
 *    <Badge>Label</Badge>
 *    <Badge colorVariant="primary" variant="solid">Label</Badge>
 *  </>
 * );
 * ```
 *
 * @component
 */

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      children,
      colorVariant,
      density = 'comfortable',
      prefix,
      variant,
      ...props
    }: BadgeProps,
    ref
  ) => {
    const themeSize = useSize();
    const prefixProps = {size: 16};

    return (
      <BadgeView
        {...props}
        colorVariant={colorVariant}
        density={density}
        ref={ref}
        size={themeSize}
        variant={variant}
        whiteSpace="nowrap"
      >
        {prefix ? renderElement(prefix, prefixProps) : null}
        <Text>{children}</Text>
      </BadgeView>
    );
  }
);

Badge.displayName = 'Badge';
Badge.toString = BadgeView.toString;

export default Badge;
